/**
 * Feb 2026 — UnmatchedCustomerPopover
 *
 * Shown on GST Sales list rows whose `customer_name` does NOT match a
 * client record. Renders the name as a clickable button; clicking
 * opens a popover offering to create a client with that name, after
 * which the parent registers the new client-id and opens the
 * Customer Overview dialog seamlessly.
 */
import { useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

export default function UnmatchedCustomerPopover({
  ws,
  docId,
  customerName,
  onLinked,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    const nm = (customerName || "").trim();
    if (!nm) return;
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${ws}/clients`, { name: nm });
      const client = r.data || {};
      toast.success(`Created client "${nm}"`);
      setOpen(false);
      if (onLinked) onLinked(client);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not create client"));
    } finally {
      setBusy(false);
    }
  };

  const btnCls = compact
    ? "text-left text-foreground/80 hover:text-primary hover:underline decoration-dotted underline-offset-2 truncate"
    : "text-left text-foreground hover:text-primary hover:underline decoration-dotted underline-offset-2 truncate";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={btnCls}
          title="No client record yet"
          data-testid={`gst-customer-unmatched-${docId}`}
        >
          {customerName || "—"}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-3 w-72 z-[1300]"
        onClick={(e) => e.stopPropagation()}
        data-testid={`gst-customer-unmatched-popover-${docId}`}
      >
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
          No client record
        </div>
        <div className="text-xs text-foreground break-words mb-2">
          <b>{customerName}</b> isn't linked to a client yet.
        </div>
        <Button
          size="sm"
          className="h-7 rounded-none text-xs w-full"
          disabled={busy}
          onClick={create}
          data-testid={`gst-customer-unmatched-create-${docId}`}
        >
          <UserPlus className="w-3 h-3 mr-1" />
          {busy ? "Creating..." : "Create client with this name"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
